import { useEffect, useMemo, useRef } from 'react'
import moment from 'moment'
import BookingItemDay from '../list/components/BookingItemDay'

const normalizeId = value => (value === undefined || value === null ? '' : String(value))

const getResourceId = resource => normalizeId(resource?._id ?? resource?.id ?? resource?.resourceId)

const toMoment = value => (value ? moment(value) : null)

function InteractiveDayView({ resources = [], startDate, bookings = [], draftBooking = null, onSlotSelect }) {
	const containerRef = useRef(null)
	const firstBookingRef = useRef(null)
	const hours = useMemo(() => Array.from({ length: 24 }, (_, index) => moment().hours(index).minutes(0).format('HH:mm')), [])

	const dayStart = useMemo(() => (startDate ? startDate.clone().startOf('day') : moment().startOf('day')), [startDate])
	const currentDate = dayStart.format('YYYY-MM-DD')

	const getBookingsForResource = (resourceId, date) => {
		return bookings.filter(booking => {
			const bookingResourceId = normalizeId(booking.resourceId ?? booking.resource?.id ?? booking.resource?._id)
			return bookingResourceId === resourceId && moment(booking.start).format('YYYY-MM-DD') === date
		})
	}

	const getBookingStyle = booking => {
		const startTime = moment(booking.start)
		const endTime = moment(booking.end)
		const totalStartMinutes = startTime.hours() * 60 + startTime.minutes()
		const totalEndMinutes = endTime.hours() * 60 + endTime.minutes()
		const durationMinutes = Math.max(totalEndMinutes - totalStartMinutes, 15)
		const startSlot = Math.floor(totalStartMinutes / 15)
		const slotsSpan = Math.ceil(durationMinutes / 15)
		return { startSlot, slotsSpan }
	}

	const draft = useMemo(() => {
		if (!draftBooking?.start || !draftBooking?.end || !draftBooking?.resourceId) return null
		const start = toMoment(draftBooking.start)
		if (!start || !start.isSame(dayStart, 'day')) return null
		return {
			...draftBooking,
			start,
			end: toMoment(draftBooking.end) ?? start.clone().add(1, 'hour'),
		}
	}, [draftBooking, dayStart])

	const currentDateBookings = useMemo(
		() =>
			bookings.filter(booking => {
				const bookingDate = moment(booking.start)
				return bookingDate.isSame(dayStart, 'day')
			}),
		[bookings, dayStart]
	)

	const earliestBooking = currentDateBookings.length
		? currentDateBookings.reduce((earliest, booking) => (booking.start < earliest.start ? booking : earliest))
		: null




	useEffect(() => {
		const container = containerRef.current
		if (!container) return

		if (firstBookingRef.current) {
			const scrollLeft = firstBookingRef.current.offsetLeft - 300
			container.scrollLeft = Math.max(0, scrollLeft)
			return
		}

		const middleSlot = 48
		const cellWidth = 30
		const scrollLeft = middleSlot * cellWidth - container.clientWidth / 2
		container.scrollLeft = Math.max(0, scrollLeft)
	}, [startDate])

	const handleCellClick = (resource, slotIndex) => {
		if (!onSlotSelect) return
		const slotMoment = dayStart.clone().add(slotIndex * 15, 'minutes')
		onSlotSelect({
			resourceId: getResourceId(resource),
			resourceName: resource?.name || '',
			slotMoment,
		})
	}

	return (
		<div className="bg-white shadow-sm rounded-lg border border-gray-200">
			<div className="overflow-x-auto" ref={containerRef}>
				<table className="min-w-full">
					<thead className="bg-gray-50">
						<tr className="divide-x divide-gray-200">
							<th className="px-5 py-3 text-left text-sm font-semibold text-gray-900 sticky left-0 bg-gray-50 z-20 min-w-[120px] border-b border-gray-200">
								Resource
							</th>
							{hours.map((hour, index) => (
								<th key={hour} colSpan={4} className="px-2 py-3 text-center text-sm font-medium border-b border-gray-200">
									{hour}
								</th>
							))}
						</tr>
					</thead>
					<tbody className="bg-white">
						{resources.map(resource => {
							const resourceId = getResourceId(resource)
							const resourceBookings = getBookingsForResource(resourceId, currentDate)
							const draftForResource =
								draft && normalizeId(draft.resourceId) === resourceId ? draft : null
							return (
								<tr key={resourceId || resource.name} className="divide-x divide-gray-200 relative">
									<td className="px-6 py-2 text-sm font-medium text-gray-900 sticky left-0 bg-white whitespace-nowrap border-b border-gray-200 z-20">
										{resource.name}
									</td>
									{hours.map((hour, hourIndex) =>
										Array.from({ length: 4 }, (_, quarterIndex) => {
											const currentSlot = hourIndex * 4 + quarterIndex
											const bookingInSlot = resourceBookings.find(booking => {
												const { startSlot } = getBookingStyle(booking)
												return startSlot === currentSlot
											})

											const isFirstBooking =
												earliestBooking &&
												bookingInSlot &&
												bookingInSlot.start === earliestBooking.start &&
												normalizeId(bookingInSlot.resourceId) === normalizeId(earliestBooking.resourceId)

											const isDraftSlot =
												draftForResource && getBookingStyle(draftForResource).startSlot === currentSlot

											return (
												<td
													key={`${resourceId}-${hourIndex}-${quarterIndex}`}
													className={`px-1 py-2 text-sm border-b border-gray-200 border-r border-gray-200 overflow-visible relative min-w-[30px] h-16 ${
														onSlotSelect ? 'cursor-pointer hover:bg-indigo-50' : ''
													}`}
													onClick={() => handleCellClick(resource, currentSlot)}
													ref={isFirstBooking ? firstBookingRef : null}
												>
													{bookingInSlot && (
														<div
															className="absolute top-1 left-0 h-[calc(100%-8px)] z-10"
															style={{
																width: `calc(${getBookingStyle(bookingInSlot).slotsSpan * 100}% + ${(getBookingStyle(bookingInSlot).slotsSpan - 1) * 1}px)`
															}}>
															<BookingItemDay booking={bookingInSlot} />
														</div>
													)}
													{draftForResource && isDraftSlot && (
														<div
															className="absolute top-1 left-0 h-[calc(100%-8px)] z-20"
															style={{
																width: `calc(${getBookingStyle(draftForResource).slotsSpan * 100}% + ${(getBookingStyle(draftForResource).slotsSpan - 1) * 1}px)`
															}}>
															<div className="rounded-md bg-yellow-300 text-yellow-900 text-xs font-semibold px-2 py-1 truncate shadow border border-yellow-500">
																Draft booking
															</div>
														</div>
													)}
												</td>
											)
										})
									)}
							</tr>
						)})}
					</tbody>
				</table>
			</div>
		</div>
	)
}

export default InteractiveDayView
